import React, { useEffect, useRef } from 'react'

import {
  WppAccordion,
  WppButton,
  WppDivider,
  WppFilterButton,
  WppIconDownload,
  WppIconSupport,
  WppSegmentedControl,
  WppSegmentedControlItem,
  WppTab,
  WppTabs,
  WppToggle,
  WppTopbar,
  WppTypography,
} from '@platform-ui-kit/components-library-react'
import { ECharts, EChartsOption, init } from 'echarts'
import { useTranslation } from 'react-i18next'

// import { PreviewHeader } from 'pages/preview/components/PreviewHeader/PreviewHeader'
import styles from './DataVisualizationPreview.module.scss'
import { PAGE_PREVIEW_ID } from '../consts'
import { getPreviewConfig } from '../get-preview-config'

export const DataVisualizationPreview = () => {
  const { t } = useTranslation()
  const barChartRef = useRef<HTMLDivElement>(null)
  const areaChartRef = useRef<HTMLDivElement>(null)
  const { dataVizPreviewAreaChartConfig, dataVizPreviewBarChartConfig, pagePreviewNavigation } = getPreviewConfig({ t })

  const initializeChart = ({
    chartRef,
    option,
  }: {
    chartRef: React.RefObject<HTMLDivElement>
    option: EChartsOption
  }) => {
    let chart: ECharts | undefined

    if (chartRef.current !== null) {
      chart = init(chartRef.current, {})
    }

    if (chart) {
      chart.setOption(option)
    }
  }

  useEffect(() => {
    initializeChart({ chartRef: barChartRef, option: dataVizPreviewBarChartConfig })
    initializeChart({ chartRef: areaChartRef, option: dataVizPreviewAreaChartConfig })
  }, [dataVizPreviewAreaChartConfig, dataVizPreviewBarChartConfig])

  return (
    <div className={styles.root}>
      <div id={PAGE_PREVIEW_ID}>
        {/*<PreviewHeader breadcrumbs={pagePreviewBreadcrumbs} />*/}

        <WppTopbar
          value={pagePreviewNavigation[1].value}
          navigation={pagePreviewNavigation}
          className={styles.previewHeader}
        >
          <WppTypography type="m-strong" slot="app" style={{ marginRight: 44 }}>
            {t('dataViz.marketingOps')}
          </WppTypography>
        </WppTopbar>

        <div className={styles.main}>
          <div className={styles.titleWrapper}>
            <WppTypography type="3xl-heading">{t('dataViz.branding')}</WppTypography>

            <div className={styles.buttonsWrapper}>
              <WppButton variant="secondary">{t('dataViz.cancel')}</WppButton>
              <WppButton>{t('dataViz.saveAndContinue')}</WppButton>
            </div>
          </div>

          <div className={styles.page}>
            <div className={styles.section}>
              <div className={styles.flex}>
                <WppTabs value="touchpoints" className={styles.tabs}>
                  <WppTab value="comms-task">{t('dataViz.tabs.commsTask')}</WppTab>
                  <WppTab value="audiences">{t('dataViz.tabs.audiences')}</WppTab>
                  <WppTab value="touchpoints">{t('dataViz.tabs.touchpoints')}</WppTab>
                </WppTabs>
              </div>

              <div className={styles.methodologyBlock}>
                <WppIconSupport />
                <WppTypography type="s-strong">{t('dataViz.methodology')}</WppTypography>
              </div>
            </div>
            <WppDivider className={styles.divider} />

            <div className={styles.section} style={{ alignItems: 'flex-start', marginTop: 24 }}>
              <div className={styles.left}>
                <WppTypography type="s-body">{t('dataViz.touchpointsHelp')}</WppTypography>

                <WppAccordion text={t('dataViz.accordions.driveBrandConnection')} expanded withDivider={false}>
                  <div className={styles.accordionContent}>
                    <WppToggle labelConfig={{ text: t('dataViz.audiences.professionals') }} checked required />
                  </div>
                </WppAccordion>
                <WppAccordion text={t('dataViz.accordions.buildTrust')} expanded withDivider={false}>
                  <div className={styles.accordionContent}>
                    <WppToggle
                      labelConfig={{ text: t('dataViz.audiences.individualEntrepreneurs') }}
                      checked
                      required
                    />
                    <WppToggle labelConfig={{ text: t('dataViz.audiences.students') }} required />
                  </div>
                </WppAccordion>
                <WppAccordion text={t('dataViz.accordions.promptTrial')} expanded withDivider={false}>
                  <div className={styles.accordionContent}>
                    <WppToggle
                      labelConfig={{ text: t('dataViz.audiences.individualEntrepreneurs') }}
                      checked
                      required
                    />
                    <WppToggle labelConfig={{ text: t('dataViz.audiences.students') }} required />
                  </div>
                </WppAccordion>
              </div>

              <div className={styles.right}>
                <div className={styles.flex}>
                  <div className={styles.filtersWrapper}>
                    <WppSegmentedControl value="graph">
                      {[
                        { id: 'table', title: t('dataViz.viewModes.table') },
                        { id: 'graph', title: t('dataViz.viewModes.graph') },
                      ].map(tab => (
                        <WppSegmentedControlItem key={`item-${tab.id}`} value={tab.id}>
                          {tab.title}
                        </WppSegmentedControlItem>
                      ))}
                    </WppSegmentedControl>
                    <WppFilterButton>{t('dataViz.filters')}</WppFilterButton>
                    <WppToggle labelConfig={{ text: t('dataViz.showSelectedOnly') }} />
                  </div>

                  <WppButton variant="secondary">
                    <WppIconDownload slot="icon-start" />
                    {t('dataViz.exportGraph')}
                  </WppButton>
                </div>
                <div>
                  <div ref={barChartRef} style={{ width: '825px', height: '500px' }} />
                </div>
              </div>
            </div>
          </div>

          <div className={`${styles.page} ${styles.graph}`}>
            <div className={styles.flex}>
              <WppTypography type="l-strong">{t('dataViz.incomeDistribution')}</WppTypography>
              <WppButton variant="secondary">
                <WppIconDownload slot="icon-start" />
                {t('dataViz.exportGraph')}
              </WppButton>
            </div>
            <div>
              <div ref={areaChartRef} style={{ width: '100%', height: '500px' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
